import {Component, forwardRef, Input, OnChanges, SimpleChanges} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR, ReactiveFormsModule,
  Validators
} from '@angular/forms';
import {environment} from '../../../../environments/environment';
import {IfeFormat} from '../../../target_systems';
import {LiltDomain, LiltModel} from '../../../lilt-domains.types';

// vtk_template values (lower-cased for case-insensitive comparison — RAVE templates
// are stored upper-case, Thales/Panasonic ones lower-case in target_systems.ts) whose
// VTK templates honor the dyn_subtitle_position override.
const DYN_SUBTITLE_POSITION_TEMPLATES = new Set([
  // Thales classic (SST; VTT companion when generate_webvtt is set)
  'thales-sd-mpeg1-ts', 'thales-sd-mpeg2-ts', 'thales-sd-avc-ts',
  'thales-720p-constrained-avc-ts', 'thales-720p-avc-ts', 'thales-1080p-avc-ts',
  // Thales Avant Up (WebVTT only)
  'thales-avant-up-hevc-480p', 'thales-avant-up-hevc-480p-crf',
  'thales-avant-up-hevc-720p', 'thales-avant-up-hevc-720p-crf',
  'thales-avant-up-hevc-1080p', 'thales-avant-up-hevc-1080p-crf',
  'thales-avant-up-hevc-2160p', 'thales-avant-up-hevc-2160p-crf',
  'thales-avant-up-avod-hevc-540p', 'thales-avant-up-avod-hevc-720p',
  'thales-avant-up-avod-hevc-1080p',
  // Safran RAVE (SST)
  'rave-sd', 'rave-720p', 'rave-1080p',
  'rave-sd-simple-filename', 'rave-720p-simple-filename', 'rave-1080p-simple-filename',
  // Panasonic (SST, m4 also ships VTT sidecars)
  'panasonic-h7', 'panasonic-h8', 'panasonic-m1', 'panasonic-m2', 'panasonic-m4', 'panasonic-m4-cbr',
  'ex2-m1', 'ex2-m2', 'ex2-m4',
  // Panasonic NEXT (WebVTT only)
  'panasonic-h7-next', 'panasonic-h8-next',
  // Delta DFP / Intelsat VOD + safety (WebVTT) — new in #529
  'delta-dfp-vod', 'delta-dfp-safety', 'delta-dfp-safety-a321neo',
  'delta-intelsat-vod', 'delta-intelsat-safety', 'delta-intelsat-safety-a321neo',
  // Delta FlytEdge (WebVTT) — new in #529
  'delta-flytedge', 'delta-flytedge-external',
]);

@Component({
  selector: 'app-default-format-specific',
  templateUrl: './default-format-specific.component.html',
  styleUrls: ['./default-format-specific.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DefaultFormatSpecificComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: DefaultFormatSpecificComponent,
      multi: true
    }
  ],
  imports: [
    FormsModule,
    ReactiveFormsModule,
]
})
export class DefaultFormatSpecificComponent implements ControlValueAccessor, OnChanges {

  @Input()
  parentForm: FormGroup;

  @Input() ifeFormat: IfeFormat;
  @Input() poDestination: string;
  // Lilt domain chosen at PO level. The item picks its target languages from
  // this domain's models. Null until the operator picks one.
  @Input() liltDomain: LiltDomain | null = null;

  form: FormGroup;

  // Models in the input domain that match our locked source language (en-US).
  // Recomputed whenever the input domain changes.
  liltAvailableModels: LiltModel[] = [];

  protected readonly environment = environment;

  changed: any = () => {
  };
  touched: any = () => {
  };

  constructor(private formBuilder: FormBuilder) {
    this.form = this.formBuilder.group({});
    this.form.valueChanges.subscribe((val) => {
      if (this.touched) {
        this.touched();
      }
      if (this.changed) {
        this.changed(val);
      }
    });
  }

  writeValue(value: any): void {
    if (value) {
      this.form.patchValue(value, {emitEvent: false});
    }
  }

  registerOnChange(fn: any): void {
    this.changed = fn;
  }

  registerOnTouched(fn: any): void {
    this.touched = fn;
  }

  setDisabledState ?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  validate({value}: FormControl) {
    const isNotValid = !this.form.valid
    return isNotValid && {
      invalid: true
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.ifeFormat && this.form) {
      console.log('Been there done that')
      if (this.ifeFormat.vtk_template === 'nice_media') {
        if (!('license_expiration_date' in this.form.controls)) {
          const twoMonthsFromNow = new Date(new Date().getUTCFullYear(), new Date().getUTCMonth() + 2, 1)
          this.form.addControl('license_expiration_date',
            new FormControl(
              `${twoMonthsFromNow.getDate()}.${twoMonthsFromNow.getMonth()}.${twoMonthsFromNow.getFullYear()}`,
              [Validators.required]));
        }
      } else {
        this.form.removeControl('license_expiration_date');
      }
      if (this.ifeFormat.vtk_template === 'delta-flytedge') {
        if (!('dfe_ca_bits' in this.form.controls)) {
          this.form.addControl('dfe_ca_bits', new FormControl('48'));
        }
        if (!('dfe_key_id' in this.form.controls)) {
          this.form.addControl('dfe_key_id', new FormControl('5EF8A7AB75624939AF735ACC42F5C54D'));
        }
        if (!('dfe_key' in this.form.controls)) {
          this.form.addControl('dfe_key', new FormControl('AC9FC2B4BD3B258D53E6AC8AB6E14C07'));
        }
        if (!('dfe_disable_drm' in this.form.controls)) {
          this.form.addControl('dfe_disable_drm', new FormControl('0'));
        }
      } else {
        this.form.removeControl('dfe_ca_bits')
        this.form.removeControl('dfe_key_id')
        this.form.removeControl('dfe_key')
        this.form.removeControl('dfe_disable_drm')
      }

      // Older Thales formats only: optionally also generate WebVTT
      if (this.ifeFormat.vtk_template.startsWith('thales-')
        && this.ifeFormat.vtk_template.indexOf('avant-up') === -1
      ) {
        if(!('generate_webvtt' in this.form.controls)) {
          this.form.addControl('generate_webvtt', new FormControl(false));
        }
      } else {
        this.form.removeControl('generate_webvtt');
      }

      // Profiles whose VTK templates support overriding dynamic (burnt-in) subtitle position.
      if (DYN_SUBTITLE_POSITION_TEMPLATES.has(this.ifeFormat.vtk_template.toLowerCase())) {
        if (!('dyn_subtitle_position' in this.form.controls)) {
          this.form.addControl('dyn_subtitle_position', new FormControl(''));
        }
      } else {
        this.form.removeControl('dyn_subtitle_position');
      }

      if (this.ifeFormat.vtk_template.startsWith('thales-avant-up-avod-')) {
        if (!('thales_fastpath_airline' in this.form.controls)) {
          this.form.addControl('thales_fastpath_airline', new FormControl('', [Validators.required]));
        }
        if (!('video_bitrate' in this.form.controls)) {
          this.form.addControl('video_bitrate', new FormControl('', [
            Validators.min(1000000),
            Validators.max(100000000)
          ]));
        }

      } else {
        this.form.removeControl('thales_fastpath_airline')
        this.form.removeControl('video_bitrate')
      }

      // Lilt translation destination — domain/src-lang are PO-level (see DefaultPoComponent).
      // This item just picks target languages from the chosen domain's models + optional refs.
      if (this.poDestination === 'lilt') {
        if (!('lilt_language_pairs' in this.form.controls)) {
          this.form.addControl('lilt_language_pairs', new FormControl([], [
            (control) => (!control.value || control.value.length === 0) ? {required: true} : null
          ]));
        }
        if (!('lilt_reference_urls' in this.form.controls)) {
          this.form.addControl('lilt_reference_urls', new FormControl('', [
            (control) => {
              const lines = (control.value || '').split('\n').map((l: string) => l.trim()).filter((l: string) => l !== '');
              const bad = lines.filter((l: string) => !/^s3:\/\/[^/]+\/.+/.test(l));
              return bad.length ? {invalidS3Url: bad} : null;
            }
          ]));
        }
        this.recomputeAvailableModels();
      } else {
        this.form.removeControl('lilt_language_pairs');
        this.form.removeControl('lilt_reference_urls');
      }

      if (this.ifeFormat.vtk_template === 'mezzanine_default') {
        if (!(this.form.controls['preroll'])) {
          this.form.addControl('preroll', new FormControl(''));
        }
        if (!(this.form.controls['card_url'])) {
          this.form.addControl('card_url', new FormControl(''));
        }
        if (!(this.form.controls['card_duration'])) {
          this.form.addControl('card_duration', new FormControl(''));
        }
      } else {
        this.form.removeControl('preroll')
        this.form.removeControl('card_url')
        this.form.removeControl('card_duration')
      }

    }
  }

  private recomputeAvailableModels(): void {
    // Filter to source = en-US (current locked source language).
    this.liltAvailableModels = (this.liltDomain?.models ?? []).filter(
      m => m.src_lang === 'en' && m.src_locale === 'US'
    );
    // Drop language-pair selections that no longer match the (possibly changed) domain.
    const pairsCtrl = this.form.controls['lilt_language_pairs'];
    if (pairsCtrl) {
      const validIds = new Set(this.liltAvailableModels.map(m => m.memory_id));
      const filtered = (pairsCtrl.value || []).filter((p: any) => validIds.has(p.memoryId));
      if (filtered.length !== (pairsCtrl.value || []).length) {
        pairsCtrl.setValue(filtered);
      }
    }
  }

  // Template helpers for the target-language multi-select.
  isModelSelected(model: LiltModel): boolean {
    const pairs = this.form.controls['lilt_language_pairs']?.value || [];
    return pairs.some((p: any) => p.memoryId === model.memory_id);
  }

  toggleModel(model: LiltModel): void {
    const ctrl = this.form.controls['lilt_language_pairs'];
    if (!ctrl) return;
    const pairs = [...(ctrl.value || [])];
    const idx = pairs.findIndex((p: any) => p.memoryId === model.memory_id);
    if (idx >= 0) {
      pairs.splice(idx, 1);
    } else {
      pairs.push({
        trgLang: model.trg_lang,
        trgLocale: model.trg_locale,
        memoryId: model.memory_id,
      });
    }
    ctrl.setValue(pairs);
  }

  formatModelLabel(model: LiltModel): string {
    const trg = model.trg_locale ? `${model.trg_lang}-${model.trg_locale}` : model.trg_lang;
    return trg;
  }

}
